/**
 * CUSTOM TOAST NOTIFICATION SYSTEM
 * =================================
 * 
 * This replaces Sonner with a lightweight, dependency-free toast system.
 * It provides the same functionality but with complete control and no external dependencies.
 * 
 * Features:
 * - Multiple toast types (success, error, info, warning)
 * - Auto-dismiss with configurable duration
 * - Manual dismiss with close button
 * - Smooth animations
 * - Accessible with ARIA attributes
 * - Position customization
 * - Stacking support
 */

import { createContext, useContext, useCallback, useReducer } from 'react';
import { XIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon } from '../icons';

// Toast types
export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastState {
  toasts: Toast[];
}

type ToastAction = 
  | { type: 'ADD_TOAST'; payload: Toast }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'CLEAR_ALL' };

// Toast context
const ToastContext = createContext<{
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearAll: () => void;
} | null>(null);

// Toast reducer
function toastReducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.payload]
      };
    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(toast => toast.id !== action.payload)
      };
    case 'CLEAR_ALL':
      return {
        ...state,
        toasts: []
      };
    default:
      return state;
  }
}

// Generate unique IDs for toasts
let toastIdCounter = 0;
const generateToastId = () => `toast-${++toastIdCounter}`;

// Toast provider component
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(toastReducer, { toasts: [] });

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const newToast: Toast = {
      id: generateToastId(),
      duration: 5000, // 5 seconds default
      dismissible: true,
      ...toast,
    };

    dispatch({ type: 'ADD_TOAST', payload: newToast });

    // Auto-dismiss after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        dispatch({ type: 'REMOVE_TOAST', payload: newToast.id });
      }, newToast.duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  return (
    <ToastContext.Provider value={{ toasts: state.toasts, addToast, removeToast, clearAll }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

// Toast container component
function ToastContainer() {
  const context = useContext(ToastContext);
  if (!context) return null;

  const { toasts } = context;

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}

// Individual toast item component
function ToastItem({ toast }: { toast: Toast }) {
  const context = useContext(ToastContext);
  if (!context) return null;

  const { removeToast } = context;

  const handleDismiss = () => {
    removeToast(toast.id);
  };

  const handleAction = () => {
    toast.action?.onClick();
    removeToast(toast.id);
  };

  // Get icon and styles based on toast type
  const getToastStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircleIcon className="h-5 w-5 text-green-500" />,
          className: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
        };
      case 'error':
        return {
          icon: <AlertCircleIcon className="h-5 w-5 text-red-500" />,
          className: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
        };
      case 'warning':
        return {
          icon: <AlertCircleIcon className="h-5 w-5 text-yellow-500" />,
          className: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200'
        };
      case 'info':
      default:
        return {
          icon: <InfoIcon className="h-5 w-5 text-blue-500" />,
          className: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
        };
    }
  };

  const { icon, className } = getToastStyles(toast.type);

  return (
    <div
      className={`
        pointer-events-auto relative flex w-full max-w-sm rounded-lg border p-4 shadow-lg transition-all duration-300 ease-in-out
        animate-in slide-in-from-top-5 fade-in-0
        ${className}
      `}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Icon */}
      <div className="flex-shrink-0 mr-3">
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">
          {toast.title}
        </div>
        {toast.description && (
          <div className="mt-1 text-sm opacity-90">
            {toast.description}
          </div>
        )}
        {toast.action && (
          <div className="mt-2">
            <button
              onClick={handleAction}
              className="text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {toast.action.label}
            </button>
          </div>
        )}
      </div>

      {/* Close button */}
      {toast.dismissible && (
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 ml-2 inline-flex rounded-md p-1.5 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:hover:bg-white/5"
          aria-label="Close notification"
        >
          <XIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// Custom hook to use toast
// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider; only fast refresh is affected.
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const { addToast, removeToast, clearAll } = context;

  // Convenience methods for different toast types
  const toast = useCallback((options: Omit<Toast, 'id'>) => {
    addToast(options);
  }, [addToast]);

  const success = useCallback((title: string, description?: string) => {
    addToast({ 
      type: 'success', 
      title, 
      ...(description && { description })
    });
  }, [addToast]);

  const error = useCallback((title: string, description?: string) => {
    addToast({ 
      type: 'error', 
      title, 
      ...(description && { description })
    });
  }, [addToast]);

  const info = useCallback((title: string, description?: string) => {
    addToast({ 
      type: 'info', 
      title, 
      ...(description && { description })
    });
  }, [addToast]);

  const warning = useCallback((title: string, description?: string) => {
    addToast({ 
      type: 'warning', 
      title, 
      ...(description && { description })
    });
  }, [addToast]);

  return {
    toast,
    success,
    error,
    info,
    warning,
    dismiss: removeToast,
    clear: clearAll,
  };
}

// CSS for animations (add to your global CSS)
export const toastStyles = `
  @keyframes slide-in-from-top-5 {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes fade-in-0 {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .animate-in {
    animation-duration: 0.3s;
    animation-fill-mode: both;
  }

  .slide-in-from-top-5 {
    animation-name: slide-in-from-top-5;
  }

  .fade-in-0 {
    animation-name: fade-in-0;
  }
`;